import React from 'react';
import { Card, Grid, Group, NumberInput, Select, Text, TextInput, ThemeIcon } from '@mantine/core';
import { IconBulb, IconCalendarTime, IconCar, IconPlane, IconReceipt, IconUsers } from '@tabler/icons-react';
import { TarjetaTablaEditable } from '../../../../globales/componentes/tabla/tarjetaTablaEditable';
import type { ITablaEditable } from '../../../../globales/componentes/tabla/useEdicionFilas';
import type { IConceptoCostoPostventa, ICotizacionPostventa, UnidadTiempoPostventa } from '../../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import type { ICostosPorCategoria } from '../../../../modelos/CotizadorPostventa/entidades/IResultadoCotizacionPostventa';
import { columnasConceptos } from '../constantesTablasCostosPostventa';
import { formatoMoneda, UNIDADES_TIEMPO, type CampoCotizacionPostventa, type SeccionConceptos } from '../../interfaces';

const COLUMNAS_CONCEPTOS = columnasConceptos();

interface PasoCostosVistaProps {
    cotizacion: ICotizacionPostventa;
    conceptos: Record<SeccionConceptos, ITablaEditable<IConceptoCostoPostventa>>;
    errores: Record<string, string>;
    alCambiar: (campo: CampoCotizacionPostventa, valor: string | number) => void;
}

const Tarjeta: React.FC<{ titulo: string; icono: React.ReactNode; children: React.ReactNode }> = ({ titulo, icono, children }) => (
    <Card shadow="sm" p="md" radius="md" withBorder h="100%">
        <Group gap="xs" mb="sm">
            <ThemeIcon variant="light" color="primary" radius="xl" size="md">{icono}</ThemeIcon>
            <Text fw={600} size="md">{titulo}</Text>
        </Group>
        {children}
    </Card>
);

/** Total de la sección (lo entrega el back al calcular). */
const PieTotal: React.FC<{ etiqueta: string; costos?: ICostosPorCategoria; categoria: keyof ICostosPorCategoria }> = ({ etiqueta, costos, categoria }) => (
    <Group justify="flex-end" gap="xs">
        <Text size="sm" c="dimmed">{etiqueta}:</Text>
        <Text size="sm" fw={700}>{costos ? formatoMoneda(costos[categoria]) : '-'}</Text>
    </Group>
);

const SECCIONES: { seccion: SeccionConceptos; titulo: string; icono: React.ReactNode; categoria: keyof ICostosPorCategoria }[] = [
    { seccion: 'Transportes', titulo: 'Transportes por Unidad', icono: <IconCar size={18} />, categoria: 'Transporte' },
    { seccion: 'Viaticos', titulo: 'Gastos de Viaje y Viáticos por Unidad', icono: <IconPlane size={18} />, categoria: 'Viaticos' },
    { seccion: 'KnowHow', titulo: 'Know-How por Unidad', icono: <IconBulb size={18} />, categoria: 'KnowHow' },
    { seccion: 'OtrosCostos', titulo: 'Otros Costos por Unidad', icono: <IconReceipt size={18} />, categoria: 'Otros' }
];

/** Paso 3: mano de obra, tiempos y demás costos por unidad. */
export const PasoCostosVista: React.FC<PasoCostosVistaProps> = ({ cotizacion, conceptos, errores, alCambiar }) => {
    const costos = cotizacion.Resultado?.Costos;
    const numero = (campo: CampoCotizacionPostventa) => (v: string | number) => alCambiar(campo, v === '' ? '' : Number(v));

    return (
        <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 6 }}>
                <Tarjeta titulo="Mano de Obra por Unidad" icono={<IconUsers size={18} />}>
                    <Grid>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <NumberInput size="xs" withAsterisk label="Horas hombre total" min={0} allowNegative={false} hideControls decimalScale={1} value={cotizacion.HorasHombre} onChange={numero('HorasHombre')} error={errores.HorasHombre} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <NumberInput size="xs" withAsterisk label="Costo promedio hora hombre" min={0} allowNegative={false} hideControls prefix="$ " thousandSeparator="." decimalSeparator="," value={cotizacion.TarifaHoraHombre} onChange={numero('TarifaHoraHombre')} error={errores.TarifaHoraHombre} />
                        </Grid.Col>
                        <Grid.Col span={12}>
                            <TextInput size="xs" label="Detalle" maxLength={200} value={cotizacion.DetalleManoObra} onChange={(e) => alCambiar('DetalleManoObra', e.currentTarget.value)} />
                        </Grid.Col>
                    </Grid>
                    <PieTotal etiqueta="Total mano de obra por unidad" costos={costos} categoria="ManoObra" />
                </Tarjeta>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
                <Tarjeta titulo="Tiempos por Unidad" icono={<IconCalendarTime size={18} />}>
                    <Grid>
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                            <Select size="xs" withAsterisk label="Unidad" data={UNIDADES_TIEMPO} value={cotizacion.UnidadTiempo} onChange={(v) => alCambiar('UnidadTiempo', (v ?? 'Días') as UnidadTiempoPostventa)} allowDeselect={false} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                            <NumberInput size="xs" withAsterisk label="Fabricación" min={0} allowNegative={false} hideControls value={cotizacion.TiempoFabricacion} onChange={numero('TiempoFabricacion')} error={errores.TiempoFabricacion} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                            <NumberInput size="xs" withAsterisk label="Instalación" min={0} allowNegative={false} hideControls value={cotizacion.TiempoInstalacion} onChange={numero('TiempoInstalacion')} error={errores.TiempoInstalacion} />
                        </Grid.Col>
                    </Grid>
                    <Group justify="flex-end" gap="xs" mt="xs">
                        <Text size="sm" c="dimmed">Tiempo total por unidad:</Text>
                        <Text size="sm" fw={700}>{cotizacion.Resultado ? `${cotizacion.Resultado.TiempoTotal} ${cotizacion.UnidadTiempo.toLowerCase()}` : '-'}</Text>
                    </Group>
                </Tarjeta>
            </Grid.Col>

            {SECCIONES.map(({ seccion, titulo, icono, categoria }) => (
                <Grid.Col key={seccion} span={{ base: 12, lg: 6 }}>
                    <TarjetaTablaEditable
                        titulo={titulo}
                        icono={icono}
                        tabla={conceptos[seccion]}
                        columnas={COLUMNAS_CONCEPTOS}
                        pie={<PieTotal etiqueta="Total" costos={costos} categoria={categoria} />}
                    />
                </Grid.Col>
            ))}
        </Grid>
    );
};
